import { useEffect, useRef, useState } from "react";
import { trackEvent } from "../lib/analytics";
import { getThemeColors } from "../lib/theme";

const presets = [3, 5, 10, 15, 20, 30, 45, 60] as const;

const MAX_MINUTES = 180;

function clampDuration(num: number): number {
  return Math.min(Math.max(num, 1), MAX_MINUTES);
}

function sendDuration(
  val: string,
  send: (action: unknown) => void,
  setTime: (min: number) => unknown,
  onClamped?: (clamped: number) => void,
) {
  const num = parseInt(val, 10);
  if (isNaN(num)) return;
  const clamped = clampDuration(num);
  if (clamped !== num) {
    onClamped?.(clamped);
  }
  trackEvent("game-duration-change", { duration: clamped.toString() });
  send(setTime(clamped));
}

export function DurationControls({
  gameDuration,
  isDark,
  t,
  send,
  setTime,
}: {
  gameDuration: number;
  isDark: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
  send: (action: unknown) => void;
  setTime: (min: number) => unknown;
}) {
  const [customMode, setCustomMode] = useState(false);
  const localValueRef = useRef("");
  const inputRef = useRef<HTMLInputElement>(null);
  const isFocusedRef = useRef(false);
  const currentMin = Math.floor(gameDuration / 60);
  const [localValue, setLocalValue] = useState(String(currentMin));
  const isPreset = (presets as readonly number[]).includes(currentMin);
  const showInput = !isPreset || customMode;

  useEffect(() => {
    if (!isFocusedRef.current) {
      setLocalValue(String(currentMin));
    }
  }, [currentMin]);

  useEffect(() => {
    if (showInput && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [showInput]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (/^\d*$/.test(val)) {
      localValueRef.current = val;
      setLocalValue(val);
    }
  };

  const handleBlur = () => {
    const val = localValueRef.current;
    if (val === "") {
      setLocalValue(String(currentMin));
      return;
    }
    sendDuration(val, send, setTime, (clamped) => {
      setLocalValue(String(clamped));
      localValueRef.current = String(clamped);
    });
  };

  const handlePresetClick = (d: number) => {
    setCustomMode(false);
    setLocalValue(String(d));
    localValueRef.current = String(d);
    trackEvent("game-duration-change", { duration: d.toString() });
    send(setTime(d));
  };

  const theme = getThemeColors(isDark);

  return (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center w-full">
      <div className="flex flex-wrap gap-2">
        {presets.map((d) => (
          <button
            key={d}
            data-testid={`duration-preset-${d}`}
            onClick={() => handlePresetClick(d)}
            aria-pressed={currentMin === d}
            className={currentMin === d ? theme.chipActive : theme.chip}
          >
            {d}
          </button>
        ))}
      </div>
      <div className="mx-auto sm:ml-auto sm:mx-0">
        {showInput ? (
          <input
            ref={inputRef}
            data-testid="duration-input"
            type="text"
            inputMode="numeric"
            value={localValue}
            onChange={handleInputChange}
            onFocus={(e) => {
              isFocusedRef.current = true;
              e.target.select();
            }}
            onBlur={() => {
              isFocusedRef.current = false;
              handleBlur();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
            className={`w-16 px-2 py-1 text-center font-display text-xs outline-none ${theme.field}`}
          />
        ) : (
          <button
            data-testid="duration-custom-btn"
            onClick={() => setCustomMode(true)}
            className={theme.chip}
          >
            {t("lobby.settings.customDuration")}
          </button>
        )}
      </div>
    </div>
  );
}
